import { useEffect, useRef, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useAIAssistant } from "../context/AIAssistantContext";
import EntityResolutionCard from "./EntityResolutionCard";
import SmartReorderResult, { smartReorderResult } from "./SmartReorderResult";

export default function AIAssistantDrawer() {
  const { t } = useTranslation();
  const { selectedWorkspace, selectedStore, canEdit } = useAuth();
  const {
    open,
    message,
    setMessage,
    chat,
    loading,
    error,
    closeAssistant,
    clearError,
    sendMessage,
    confirmAction,
    confirmIdentities,
    isEntityOfferCurrent,
    submitReorderEdit,
    suggestions,
    newChat,
    hasPendingTask,
    restoreFailed,
    retryRestore,
  } = useAIAssistant();
  const navigate = useNavigate();
  const endRef = useRef(null);
  const [newChatWarning, setNewChatWarning] = useState(false);
  const latestTaskMessage = [...chat].reverse().find((item) => item.role !== "user" && item.taskId);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat, loading, open]);

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage();
  };

  const navigateFromAssistant = (path) => {
    closeAssistant();
    navigate(path);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={closeAssistant}
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100vw", sm: 420, md: 440, lg: 460 },
          maxWidth: "100vw",
          boxSizing: "border-box",
        },
      }}
      PaperProps={{
        sx: {
          borderLeft: { xs: "none", sm: "1px solid #e2e8f0" },
          boxShadow: "-18px 0 48px rgba(15,23,42,0.14)",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#fff" }}>
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.8,
            borderBottom: "1px solid #e2e8f0",
            bgcolor: "rgba(248,250,252,0.96)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.4}>
            <Box
              aria-hidden="true"
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                fontSize: 24,
                bgcolor: "#eef2ff",
                border: "1px solid #dbeafe",
              }}
            >
              🤖
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography fontWeight={950} noWrap>ABOS AI</Typography>
                <Chip
                  label={t(loading ? "assistant.working" : "assistant.ready")}
                  color={loading ? "warning" : "success"}
                  size="small"
                  variant="outlined"
                  sx={{ height: 24, fontWeight: 800 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {selectedStore?.name || t("nav.store")} · {selectedWorkspace?.name || t("nav.workspace")}
              </Typography>
            </Box>
            <Tooltip title={t("assistant.close")}>
              <IconButton aria-label={t("assistant.close")} onClick={closeAssistant} sx={{ width: 44, height: 44 }}>
                <CloseRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box sx={{ px: 2, py: 0.5 }}>
          <Button size="small" disabled={loading} onClick={() => hasPendingTask ? setNewChatWarning(true) : newChat()}>
            {t("assistant.persistence.newChat")}
          </Button>
        </Box>
        {newChatWarning && <Dialog open onClose={() => setNewChatWarning(false)}>
          <DialogTitle>{t("assistant.persistence.newChat")}</DialogTitle>
          <DialogContent>{t("assistant.persistence.abandonWarning")}</DialogContent>
          <DialogActions>
            <Button onClick={() => setNewChatWarning(false)}>{t("assistant.persistence.keepChat")}</Button>
            <Button onClick={() => { setNewChatWarning(false); newChat(); }}>{t("assistant.persistence.startFresh")}</Button>
          </DialogActions>
        </Dialog>}

        {error && (
          <Alert
            severity="error"
            onClose={restoreFailed ? undefined : clearError}
            action={restoreFailed ? <Button onClick={retryRestore}>{t("assistant.persistence.retry")}</Button> : undefined}
            sx={{ mx: 2, mt: 2, borderRadius: 2.5 }}
          >
            {error}
          </Alert>
        )}

        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: "auto",
            p: { xs: 2, sm: 2.5 },
          }}
        >
          {chat.length === 0 && (
            <Box sx={{ minHeight: "100%", display: "grid", placeItems: "center", textAlign: "center", py: 4 }}>
              <Box>
                <Typography aria-hidden="true" sx={{ fontSize: 48 }}>🤖</Typography>
                <Typography variant="h6" fontWeight={950} sx={{ mt: 1 }}>
                  {t("assistant.title")}
                </Typography>
                <Typography color="text.secondary" variant="body2" sx={{ mt: 1, lineHeight: 1.6 }}>
                  {t("assistant.help")}
                </Typography>
                <Stack direction="row" spacing={0.8} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 2.25 }}>
                  {suggestions.map((suggestion) => (
                    <Chip
                      key={suggestion}
                      label={suggestion}
                      onClick={() => setMessage(suggestion)}
                      sx={{
                        height: "auto",
                        maxWidth: "100%",
                        fontWeight: 700,
                        "& .MuiChip-label": { whiteSpace: "normal", py: 0.7 },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </Box>
          )}

          {chat.map((item, index) => {
            const userMessage = item.role === "user";
            const reorder = !userMessage && smartReorderResult(item);
            return (
              <Box
                key={`${item.role}-${index}`}
                sx={{ display: "flex", justifyContent: userMessage ? "flex-end" : "flex-start", mb: 1.6 }}
              >
                <Box
                  sx={{
                    maxWidth: "92%",
                    p: 1.6,
                    borderRadius: userMessage ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                    bgcolor: userMessage ? "#2563eb" : "#f8fafc",
                    color: userMessage ? "white" : "#0f172a",
                    border: userMessage ? "none" : "1px solid #e2e8f0",
                    overflowWrap: "anywhere",
                  }}
                >
                  <Typography variant="caption" fontWeight={900} sx={{ mb: 0.6, display: "block" }}>
                    {userMessage ? t("assistant.you") : `ABOS AI · ${t(`assistant.agents.${item.agent}`, { defaultValue: t("assistant.agent") })}`}
                  </Typography>
                  {reorder ? <SmartReorderResult
                    key={`${item.taskId}:${item.taskVersion}:${item.taskHash}:${reorder.status}:${reorder.response_page || 1}`}
                    result={reorder} language={item.responseLanguage}
                    canChooseTarget={!loading && item === latestTaskMessage}
                    loading={loading}
                    onEditAction={(action, targetId) => submitReorderEdit(index, action, targetId)}
                  /> : <Typography variant="body2" dir="auto" sx={{ whiteSpace: "pre-line", unicodeBidi: "plaintext", textAlign: "start", overflowWrap: "anywhere", lineHeight: 1.6 }}>
                    {item.content}
                  </Typography>}

                  {!userMessage && item.entityResolution && (
                    <EntityResolutionCard
                      key={`${item.entityResolution.taskId}:${item.entityResolution.taskVersion}:${item.entityResolution.taskHash}`}
                      offer={item.entityResolution}
                      language={item.responseLanguage}
                      disabled={loading || !canEdit}
                      stale={!isEntityOfferCurrent(item.entityResolution)}
                      onConfirm={(selections) => confirmIdentities(index, selections)}
                    />
                  )}

                  {!userMessage && item.action && !item.entityResolution && !reorder?.pending_edit && (
                    <Box sx={{ mt: 1.4, p: 1.3, borderRadius: 2.5, bgcolor: "#fff", border: "1px solid #e2e8f0" }}>
                      <Typography variant="caption" color="text.secondary">{t("assistant.action")}</Typography>
                      <Typography variant="body2" fontWeight={900}>
                        {t(`assistant.agents.${item.agent}`, { defaultValue: t("assistant.agent") })}
                      </Typography>
                      {item.requiresConfirmation && (
                        <Alert severity="warning" sx={{ mt: 1.1, borderRadius: 2 }}>
                          {t("assistant.confirmHelp")}
                        </Alert>
                      )}
                      {item.requiresConfirmation && canEdit && (
                        <Button
                          variant="contained"
                          color="warning"
                          size="small"
                          disabled={item.confirming || loading}
                          onClick={() => confirmAction(index)}
                          sx={{ mt: 1.15, textTransform: "none", fontWeight: 900 }}
                        >
                          {t(item.confirming ? "assistant.confirming" : "assistant.confirm")}
                        </Button>
                      )}
                      {item.requiresConfirmation && !canEdit && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                          {t("assistant.permission")}
                        </Typography>
                      )}
                      {item.resourcePublicId && item.agent === "quotation" && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigateFromAssistant("/quotations")}
                          sx={{ mt: 1.15, ml: item.requiresConfirmation && canEdit ? 1 : 0, textTransform: "none", fontWeight: 800 }}
                        >
                          {t("assistant.openQuotations")}
                        </Button>
                      )}
                      {item.resourcePublicId && item.agent === "customer" && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigateFromAssistant("/customers")}
                          sx={{ mt: 1.15, ml: item.requiresConfirmation && canEdit ? 1 : 0, textTransform: "none", fontWeight: 800 }}
                        >
                          {t("assistant.openCustomers")}
                        </Button>
                      )}
                      {item.resourcePublicId && item.agent === "smart_reorder" && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigateFromAssistant(`/purchase-orders/${item.resourcePublicId}`)}
                          sx={{ mt: 1.15, ml: item.requiresConfirmation && canEdit ? 1 : 0, textTransform: "none", fontWeight: 800 }}
                        >
                          {t("purchaseOrders.openPurchaseOrder")}
                        </Button>
                      )}
                    </Box>
                  )}

                  {process.env.NODE_ENV !== "production" && !userMessage && item.steps?.length > 0 && (
                    <Box sx={{ mt: 1.35, pt: 1.1, borderTop: "1px solid #e2e8f0" }}>
                      <Typography variant="caption" color="text.secondary">
                        Execution trace: {item.steps.join(" → ")}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}

          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.25, width: "fit-content", bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 3 }}>
              <CircularProgress size={17} />
              <Typography variant="body2" color="text.secondary">{t("assistant.reasoning")}</Typography>
            </Box>
          )}
          <div ref={endRef} />
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderTop: "1px solid #e2e8f0",
            bgcolor: "#fff",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              maxRows={5}
              minRows={1}
              placeholder={t("assistant.placeholder")}
              value={message}
              slotProps={{ htmlInput: { maxLength: 4000, "aria-label": t("assistant.inputLabel") } }}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage();
                }
              }}
            />
            <Tooltip title={t("assistant.send")}>
              <span>
                <IconButton
                  type="submit"
                  aria-label={t("assistant.sendLabel")}
                  disabled={loading || restoreFailed || !message.trim()}
                  color="primary"
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2.5,
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": { bgcolor: "primary.dark" },
                    "&.Mui-disabled": { bgcolor: "#e2e8f0" },
                  }}
                >
                  <SendRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}
